// ✅ src/pages/VehicleV2/LedgerMainReport.jsx
// Full Frontend with ALL linked pages connected (mapped from ASPX)
//
// Linked Pages:
//  1. /vehicle/LedgerInterReport    ← from Supplier/Vehicle/Hire grid row click (LedgerInterReport.aspx)
//  2. /vehicle/LedgerSubReport      ← from Dept/SparesReturn/OwnVeh/HireVeh row click (LedgerSubReport.aspx)
//  3. /vehicle/LedgerPrint          ← Print buttons for OwnVeh/HireVeh/CP/Dept/SparesReturn (Ledger_Print.aspx)
//  4. /vehicle/LedgerMainReport_Print ← Supplier print button (LedgerMainReport_print.aspx)
//  5. /vehicle/LedgerItemWiseReport ← ItemWise button (LedgerItemWiseReport.aspx)
//  6. /vehicle/SupplierMas          ← Supplier row "Edit" command (Supplier_Mas.aspx)

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch, FaPrint, FaFilter, FaArrowLeft, FaInfoCircle,
  FaList, FaFileExcel, FaEdit, FaEye,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/ledgerMainReport";

/* ─────────────────────── helpers ─────────────────────── */
const toStr = (v) => String(v ?? "").trim();
const pad2 = (n) => String(n).padStart(2, "0");

const fmtDDMMYYYY = (d) => {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}/${dt.getFullYear()}`;
};

const parseDDMMYYYY = (s) => {
  const v = toStr(s);
  if (!v) return null;
  const [ddS, mmS, yyyyS] = v.split("/");
  const dd = Number(ddS), mm = Number(mmS), yyyy = Number(yyyyS);
  if (!dd || !mm || !yyyy) return null;
  const dt = new Date(yyyy, mm - 1, dd);
  if (dt.getFullYear() !== yyyy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd) return null;
  dt.setHours(0, 0, 0, 0);
  return dt;
};

const today0 = () => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; };
const compareDateOnly = (a, b) => {
  if (!a || !b) return 0;
  const aa = new Date(a); const bb = new Date(b);
  aa.setHours(0, 0, 0, 0); bb.setHours(0, 0, 0, 0);
  return aa.getTime() === bb.getTime() ? 0 : aa < bb ? -1 : 1;
};
const isSelectLike = (v) => { const s = toStr(v); return !s || s.toLowerCase() === "select" || s === "%"; };

const exportToCsv = (rows, filename = "ledger.csv") => {
  if (!rows?.length) return toast.info("No rows to export");
  const headers = Object.keys(rows[0] || {});
  const esc = (v) => `"${String(v ?? "").replaceAll('"', '""')}"`;
  const csv = [headers.map(esc).join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

/* ─────────────────────── component ─────────────────────── */
export default function LedgerMainReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const btnCls = `bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white`;

  // ── session/local storage ──
  const userId      = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const branchType  = (localStorage.getItem("branchtype") || "HO").toUpperCase();
  const branchId    = Number(localStorage.getItem("branchid") || 8);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm
    ${isDark ? "bg-gray-700/30 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}`;
  const labelClass = `block text-xs font-semibold mb-1 uppercase tracking-wide
    ${isDark ? "text-gray-400" : "text-gray-500"}`;

  // ── state ──
  const [showFilters, setShowFilters]   = useState(true);
  const [loading, setLoading]           = useState(false);
  const [financeYearStart, setFinanceYearStart] = useState("");

  // dropdown data
  const [months, setMonths]                   = useState([{ label: "Select", value: "" }]);
  const [years, setYears]                     = useState([{ label: "Select", value: "" }]);
  const [branches, setBranches]               = useState([{ label: "Select", value: "" }]);
  const [vehicleMainTypes, setVehicleMainTypes] = useState([{ label: "Select", value: "" }]);
  const [vehicleTypes, setVehicleTypes]         = useState([{ label: "Select", value: "" }]);
  const [suppliers, setSuppliers]               = useState([{ label: "Select", value: "" }]);
  const [equipments]                            = useState([
    { label: "Select", value: "" },
    { label: "All", value: "A" },
    { label: "Hire Vehicle", value: "H" },
    { label: "Own Vehicle", value: "O" },
    { label: "Department", value: "2" },
    { label: "Spares Return", value: "3" },
    { label: "Crusher Plant", value: "C" },
  ]);

  // vehicle code autocomplete
  const [vehicleCodeQuery, setVehicleCodeQuery]   = useState("");
  const [vehicleCodeOptions, setVehicleCodeOptions] = useState([]);
  const vcTimer = useRef(null);

  // result rows per block
  const [ownRows, setOwnRows]                 = useState([]);
  const [cpRows, setCpRows]                   = useState([]);
  const [hireRows, setHireRows]               = useState([]);
  const [supplierRows, setSupplierRows]       = useState([]);
  const [deptRows, setDeptRows]               = useState([]);
  const [sparesReturnRows, setSparesReturnRows] = useState([]);

  // which tab to show
  const [activeBlock, setActiveBlock] = useState("OWN");

  const [filters, setFilters] = useState({
    hoBo:        branchType === "HO" ? "HO" : "BO",
    periodMode:  "PERIOD",
    month:       "",
    year:        "",
    fromDate:    fmtDDMMYYYY(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)),
    toDate:      fmtDDMMYYYY(new Date()),
    branch:      branchType !== "HO" ? String(branchId) : "",
    type:        "",      // "1"=Supplier, "2"=Equipment
    vender:      "%",
    rptType:     "",
    vehicleCode: "",
    vehicleMainType: "",
    vehicleType: "",
  });

  /* rptType options depend on type */
  const visibleRptTypeOptions = useMemo(() => {
    if (toStr(filters.type) === "1") return suppliers;
    if (toStr(filters.type) === "2") return equipments;
    return [{ label: "Select", value: "" }];
  }, [filters.type, suppliers, equipments]);

  /* ─────────────────────── validation ─────────────────────── */
  const validate = (f = filters) => {
    if (isSelectLike(f.rptType)) { toast.error("Please Select Supplier / Equipment."); return false; }

    if (f.periodMode === "PERIOD") {
      const fd = parseDDMMYYYY(f.fromDate);
      const td = parseDDMMYYYY(f.toDate);
      if (f.fromDate && !fd) { toast.error("Invalid From Date (dd/MM/yyyy)"); return false; }
      if (f.toDate && !td) { toast.error("Invalid To Date (dd/MM/yyyy)"); return false; }
      if (fd && fd > today0()) { toast.error("From Date must be ≤ today"); return false; }
      if (td && td > today0()) { toast.error("To Date must be ≤ today"); return false; }
      if (fd && td && compareDateOnly(td, fd) < 0) { toast.error("From Date must be ≤ To Date"); return false; }
    } else {
      if (isSelectLike(f.month) || isSelectLike(f.year)) { toast.error("Please select Month and Year"); return false; }
    }
    return true;
  };

  /* ─────────────────────── API calls ─────────────────────── */
  const fetchInit = async () => {
    const { data } = await axios.get(`${API_BASE}/init`, {
      params: { branchType, branchId, userId, hoBoOption: filters.hoBo === "HO" ? "H" : "B" },
    });
    if (data?.success === false) throw new Error(data?.message || "Init failed");

    setFinanceYearStart(toStr(data?.financeYearStartDate || ""));
    setMonths(data?.months?.length   ? data.months   : [{ label: "Select", value: "" }]);
    setYears(data?.years?.length     ? data.years    : [{ label: "Select", value: "" }]);
    setBranches(data?.branches?.length ? data.branches : [{ label: "Select", value: "" }]);
    setVehicleMainTypes(data?.vehicleMainTypes?.length ? data.vehicleMainTypes : [{ label: "Select", value: "" }]);
    setVehicleTypes(data?.vehicleTypes?.length ? data.vehicleTypes : [{ label: "Select", value: "" }]);
    setSuppliers(data?.suppliers?.length ? data.suppliers : [{ label: "Select", value: "" }]);
  };

  const fetchSuppliers = async (branchVal) => {
    const { data } = await axios.get(`${API_BASE}/suppliers`, { params: { branchId: branchVal || "%" } });
    if (data?.success === false) throw new Error(data?.message || "Supplier load failed");
    setSuppliers(data?.suppliers?.length ? data.suppliers : [{ label: "Select", value: "" }]);
  };

  const clearResults = () => {
    setOwnRows([]); setCpRows([]); setHireRows([]);
    setSupplierRows([]); setDeptRows([]); setSparesReturnRows([]);
  };

  const fetchList = async (f = filters) => {
    if (!validate(f)) return;
    const { data } = await axios.get(`${API_BASE}/list`, {
      params: {
        branchType,
        sessionBranchId: String(branchId || ""),
        hoBoOption:  f.hoBo === "HO" ? "H" : "B",
        periodMode:  f.periodMode,
        fromDate:    f.periodMode === "PERIOD" ? (f.fromDate || "") : "",
        toDate:      f.periodMode === "PERIOD" ? (f.toDate || "") : "",
        month:       f.periodMode === "MONTHWISE" ? (f.month || "Select") : "Select",
        year:        f.periodMode === "MONTHWISE" ? (f.year || "Select") : "Select",
        branch:      f.branch || "%",
        type:        f.type || "Select",
        vender:      f.vender || "%",
        rptType:     f.rptType || "Select",
        vehicleCode: f.vehicleCode || "",
        vehicleType: f.vehicleType || "%",
        vehicleMainType: f.vehicleMainType || "%",
      },
    });
    if (data?.success === false) throw new Error(data?.message || "Search failed");

    // Reset then fill the right block
    clearResults();

    // Backend returns named arrays: ownVehicleRows, hireVehicleRows, cpVehicleRows,
    // supplierRows, deptRows, sparesReturnRows  (as per routes/ledgerMainReport.js)
    if (data?.ownVehicleRows?.length)    { setOwnRows(data.ownVehicleRows);         setActiveBlock("OWN"); }
    if (data?.hireVehicleRows?.length)   { setHireRows(data.hireVehicleRows);        setActiveBlock("HIRE"); }
    if (data?.cpVehicleRows?.length)     { setCpRows(data.cpVehicleRows);            setActiveBlock("CP"); }
    if (data?.supplierRows?.length)      { setSupplierRows(data.supplierRows);       setActiveBlock("SUPPLIER"); }
    if (data?.deptRows?.length)          { setDeptRows(data.deptRows);               setActiveBlock("DEPT"); }
    if (data?.sparesReturnRows?.length)  { setSparesReturnRows(data.sparesReturnRows); setActiveBlock("SPARESRETURN"); }

    // legacy single-view support (if backend returns { view, rows })
    if (data?.view && data?.rows) {
      const view = toStr(data.view).toUpperCase();
      const rows = data.rows || [];
      if (view === "OWN_VEHICLE")   { setOwnRows(rows);         setActiveBlock("OWN"); }
      else if (view === "HIRE_VEHICLE") { setHireRows(rows);    setActiveBlock("HIRE"); }
      else if (view === "CRUSHER_PLANT") { setCpRows(rows);     setActiveBlock("CP"); }
      else if (view === "SUPPLIER")  { setSupplierRows(rows);   setActiveBlock("SUPPLIER"); }
      else if (view === "DEPARTMENT") { setDeptRows(rows);      setActiveBlock("DEPT"); }
      else if (view === "SPARES_RETURN") { setSparesReturnRows(rows); setActiveBlock("SPARESRETURN"); }
    }

    const total =
      (data?.ownVehicleRows?.length || 0) + (data?.hireVehicleRows?.length || 0) +
      (data?.cpVehicleRows?.length || 0)  + (data?.supplierRows?.length || 0) +
      (data?.deptRows?.length || 0)       + (data?.sparesReturnRows?.length || 0) +
      (data?.rows?.length || 0);
    toast.success(`Found ${total} record(s)`);
  };

  const fetchVehicleCodes = async (q) => {
    if (!toStr(q)) return setVehicleCodeOptions([]);
    const { data } = await axios.get(`${API_BASE}/vehicleCodes`, {
      params: { q, branchId: Number(filters.branch || branchId || 0) },
    });
    if (data?.success === false) throw new Error(data?.message);
    const items = (data?.items || []).map((x) =>
      typeof x === "string" ? { label: x, value: x } : x
    );
    setVehicleCodeOptions(items);
  };

  /* ─────────────────────── AutoPostBack equivalents ─────────────────────── */
  useEffect(() => {
    setFilters((p) => ({ ...p, rptType: "" }));
    if (toStr(filters.type) === "1") {
      fetchSuppliers(filters.branch || "%").catch((e) => toast.error(e?.message));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.type]);

  useEffect(() => {
    setVehicleCodeQuery(""); setVehicleCodeOptions([]);
    setFilters((p) => ({ ...p, vehicleCode: "" }));
    if (toStr(filters.type) === "1") {
      fetchSuppliers(filters.branch || "%").catch((e) => toast.error(e?.message));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.branch]);

  /* ─────────────────────── init ─────────────────────── */
  useEffect(() => {
    (async () => {
      try { setLoading(true); await fetchInit(); }
      catch (e) { toast.error(e?.message || "Failed to load dropdowns"); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─────────────────────── handlers ─────────────────────── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  };

  const handleVehicleCodeInput = (e) => {
    const v = e.target.value;
    setVehicleCodeQuery(v);
    setFilters((p) => ({ ...p, vehicleCode: v }));
    if (vcTimer.current) clearTimeout(vcTimer.current);
    vcTimer.current = setTimeout(() => {
      fetchVehicleCodes(v).catch((err) => toast.error(err?.message));
    }, 250);
  };

  const handleSearch = async () => {
    try { setLoading(true); clearResults(); await fetchList(filters); }
    catch (e) { toast.error(e?.message || "Search failed"); }
    finally { setLoading(false); }
  };

  /* ═══════════════════════════════════════════════════════════
     ✅  LINKED PAGE OPENERS — mapped 1-to-1 from ASPX
     ═══════════════════════════════════════════════════════════ */

  /** Build common querystring (same params every linked page needs) */
  const buildQS = (extra = {}) => {
    const qs = new URLSearchParams();
    qs.set("branchType",      branchType);
    qs.set("sessionBranchId", String(branchId || ""));
    qs.set("hoBoOption",      filters.hoBo === "HO" ? "H" : "B");
    qs.set("periodMode",      filters.periodMode);
    if (filters.periodMode === "PERIOD") {
      qs.set("fromDate", filters.fromDate || "");
      qs.set("toDate",   filters.toDate   || "");
    } else {
      qs.set("month", filters.month || "Select");
      qs.set("year",  filters.year  || "Select");
    }
    qs.set("branch",          filters.branch      || "%");
    qs.set("type",            filters.type        || "Select");
    qs.set("venderType",      filters.vender      || "%");
    qs.set("rptType",         filters.rptType     || "Select");
    qs.set("vehicleCode",     filters.vehicleCode || "");
    qs.set("vehTypeId",       filters.vehicleType     || "%");
    qs.set("vehMainTypeId",   filters.vehicleMainType || "%");
    Object.entries(extra).forEach(([k, v]) => qs.set(k, String(v ?? "")));
    return qs.toString();
  };

  const popup = (url) =>
    window.open(url, "_blank", "width=1100,height=900,top=60,left=100,scrollbars=yes,resizable=yes");

  /* ──────────────────────────────────────────────────────────
     1. LedgerInterReport  (old: LedgerInterReport.aspx)
        Triggered from: Supplier, OwnVehicle, HireVehicle, Dept row click
        Passes: code (supplier/vehicle code) + Type=SUPPLIER
     ────────────────────────────────────────────────────────── */
  const openLedgerInterReport = (row, block) => {
    const code =
      toStr(row?.Code || row?.SupplierCode || row?.fld_suppliercode || row?.VehicleCode || "");
    const qs = buildQS({ code, Type: "SUPPLIER", block });
    popup(`/vehicle/LedgerInterReport?${qs}`);
  };

  /* ──────────────────────────────────────────────────────────
     2. LedgerSubReport  (old: LedgerSubReport.aspx)
        Triggered from: Dept, SparesReturn, OwnVehicle, HireVehicle row click
        Passes: code + Type (rptType value from current filter)
     ────────────────────────────────────────────────────────── */
  const openLedgerSubReport = (row, block) => {
    const code = toStr(
      row?.Code || row?.DeptCode || row?.fld_deptid || row?.VehicleCode || row?.VehicleNumber || ""
    );
    const qs = buildQS({ code, Type: filters.rptType || block, block });
    popup(`/vehicle/LedgerSubReport?${qs}`);
  };

  /* ──────────────────────────────────────────────────────────
     3. Ledger_Print  (old: Ledger_Print.aspx)
        Triggered from print buttons on OwnVehicle, HireVehicle,
        CrusherPlant, Dept, SparesReturn blocks.
        Type param values:
          OWN/HIRE/CP    → Type=3
          DEPT (all)     → Type=1 | Type=2 | Type=3 (depending on rptType)
          SPARESRETURN   → Type=1 | Type=2 | Type=3 | Type=4
     ────────────────────────────────────────────────────────── */
  const resolvePrintType = (block) => {
    const rpt = filters.rptType;
    if (block === "OWN" || block === "HIRE" || block === "CP") return "3";
    if (block === "DEPT") {
      if (rpt === "2") return "2";
      if (rpt === "3") return "3";
      return "1"; // default Dept
    }
    if (block === "SPARESRETURN") {
      if (rpt === "A") return "1";
      if (rpt === "2") return "2";
      if (rpt === "3") return "4";
      return "3";
    }
    return "3";
  };

  const openLedgerPrint = (block) => {
    if (!validate()) return;
    const printType = resolvePrintType(block);
    const qs = buildQS({ block, PrintType: printType });
    popup(`/vehicle/Ledger_Print?${qs}`);
  };

  /* ──────────────────────────────────────────────────────────
     4. LedgerMainReport_Print  (old: LedgerMainReport_print.aspx)
        Triggered: "Print Supplier" button (Imgbtn_printsupplier_Click)
        Used when Type=Supplier
     ────────────────────────────────────────────────────────── */
  const openLedgerMainPrint = () => {
    if (!validate()) return;
    const qs = buildQS({ block: "SUPPLIER" });
    popup(`/vehicle/LedgerMainReport_Print?${qs}`);
  };

  /* ──────────────────────────────────────────────────────────
     5. LedgerItemWiseReport  (old: LedgerItemWiseReport.aspx)
        Triggered: "ItemWise" button (ibtn_ItemWise_Click)
        Only visible when financeYear >= 01/04/2015
     ────────────────────────────────────────────────────────── */
  const openItemWisePage = () => {
    if (!validate()) return;
    const qs = buildQS({ block: "ITEMWISE" });
    popup(`/vehicle/LedgerItemWiseReport?${qs}`);
  };

  /* ──────────────────────────────────────────────────────────
     6. SupplierMas  (old: Supplier_Mas.aspx)
        Triggered: "Edit" command in Supplier grid row
        (gv_supplier_RowCommand with command "EDIT")
     ────────────────────────────────────────────────────────── */
  const openSupplierMas = (row) => {
    const code = toStr(row?.Code || row?.SupplierCode || row?.fld_suppliercode || "");
    const qs = buildQS({ supplierCode: code });
    popup(`/vehicle/SupplierMas?${qs}`);
  };

  /* ─────────────────────── derived ─────────────────────── */
  const hasAnyResults =
    ownRows.length || cpRows.length || hireRows.length ||
    supplierRows.length || deptRows.length || sparesReturnRows.length;

  // Which blocks have data (for tab buttons)
  const blocks = [
    { key: "OWN",         label: "Own Vehicle",   rows: ownRows },
    { key: "HIRE",        label: "Hire Vehicle",  rows: hireRows },
    { key: "CP",          label: "Crusher Plant", rows: cpRows },
    { key: "SUPPLIER",    label: "Supplier",      rows: supplierRows },
    { key: "DEPT",        label: "Department",    rows: deptRows },
    { key: "SPARESRETURN",label: "Spares Return", rows: sparesReturnRows },
  ].filter((b) => b.rows.length > 0);

  // Current block rows
  const currentRows =
    activeBlock === "OWN"         ? ownRows :
    activeBlock === "HIRE"        ? hireRows :
    activeBlock === "CP"          ? cpRows :
    activeBlock === "SUPPLIER"    ? supplierRows :
    activeBlock === "DEPT"        ? deptRows :
    activeBlock === "SPARESRETURN"? sparesReturnRows : [];

  // Show ItemWise button only when finance year >= 01/04/2015
  const showItemWise = (() => {
    if (!financeYearStart) return true; // default show
    const d = parseDDMMYYYY(financeYearStart);
    const cutoff = new Date(2015, 3, 1); // 01/04/2015
    return d && d >= cutoff;
  })();

  /* ─────────────────────── table helpers ─────────────────────── */
  const tdCls = `px-3 py-2 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-800"}`;
  const thCls = `px-3 py-2 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap`;

  /* ════════════════════════════════════════════════════════════════
     VEHICLE COST TABLE (Own / Hire / Crusher Plant)
     Row actions:
       View → openLedgerInterReport  (LedgerInterReport.aspx)
       Sub  → openLedgerSubReport    (LedgerSubReport.aspx)
       Print→ openLedgerPrint        (Ledger_Print.aspx)
     ════════════════════════════════════════════════════════════════ */
  const renderVehicleTable = (rows, block) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className={`${btnCls} sticky top-0`}>
          <tr>
            {["#","Code","Vehicle Name","Vehicle Number",
              "Work Hr","Work Km",
              "Diesel Qty","Diesel Cost","HSD C-FORM Qty","HSD C-FORM Cost",
              "Oil Qty","Oil Cost","Spares Qty","Spares Cost",
              "Tyre Qty","Tyre Cost","Branch","Total",
              "View","Sub","Print"].map((h) => (
              <th key={h} className={thCls}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className={`divide-y ${isDark ? "divide-gray-700" : "divide-gray-200"}`}>
          {rows.map((r, idx) => (
            <tr key={`${block}-${idx}`}
              className={`transition-colors ${idx % 2 === 0
                ? isDark ? "bg-gray-800" : "bg-white"
                : isDark ? "bg-gray-800/60" : "bg-gray-50"
              } hover:${isDark ? "bg-gray-700" : "bg-blue-50"}`}
            >
              <td className={`${tdCls} text-center`}>{idx + 1}</td>
              <td className={tdCls}>{toStr(r?.Code)}</td>
              <td className={tdCls}>{toStr(r?.VehicleName)}</td>
              <td className={tdCls}>{toStr(r?.VehicleNumber)}</td>
              <td className={`${tdCls} text-right`}>{toStr(r?.fld_workHr)}</td>
              <td className={`${tdCls} text-right`}>{toStr(r?.fld_workKm)}</td>
              <td className={`${tdCls} text-right`}>{toStr(r?.DieselQty)}</td>
              <td className={`${tdCls} text-right`}>{toStr(r?.DieselCost)}</td>
              <td className={`${tdCls} text-right`}>{toStr(r?.HSDCFORMQty)}</td>
              <td className={`${tdCls} text-right`}>{toStr(r?.HSDCFORMCost)}</td>
              <td className={`${tdCls} text-right`}>{toStr(r?.OilQty || r?.Oilqty)}</td>
              <td className={`${tdCls} text-right`}>{toStr(r?.OilCost)}</td>
              <td className={`${tdCls} text-right`}>{toStr(r?.SparesQty || r?.Sparesqty)}</td>
              <td className={`${tdCls} text-right`}>{toStr(r?.Sparescost || r?.SparesCost)}</td>
              <td className={`${tdCls} text-right`}>{toStr(r?.TyreQty || r?.Tyreqty)}</td>
              <td className={`${tdCls} text-right`}>{toStr(r?.Tyrecost)}</td>
              <td className={tdCls}>{toStr(r?.Branch)}</td>
              <td className={`${tdCls} text-right font-semibold`}>{toStr(r?.Total)}</td>

              {/* ✅ View → LedgerInterReport.aspx */}
              <td className="px-2 py-1 text-center">
                <button
                  onClick={() => openLedgerInterReport(r, block)}
                  title="View Ledger Inter Report"
                  className="p-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <FaEye className="w-3 h-3" />
                </button>
              </td>

              {/* ✅ Sub → LedgerSubReport.aspx */}
              <td className="px-2 py-1 text-center">
                <button
                  onClick={() => openLedgerSubReport(r, block)}
                  title="View Ledger Sub Report"
                  className="p-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <FaList className="w-3 h-3" />
                </button>
              </td>

              {/* ✅ Print → Ledger_Print.aspx */}
              <td className="px-2 py-1 text-center">
                <button
                  onClick={() => openLedgerPrint(block)}
                  title="Print"
                  className={`p-2 rounded-lg ${btnCls}`}
                >
                  <FaPrint className="w-3 h-3" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  /* ════════════════════════════════════════════════════════════════
     SUPPLIER TABLE  (gv_supplier)
     Row actions:
       View → openLedgerInterReport  (LedgerInterReport.aspx)
       Edit → openSupplierMas        (Supplier_Mas.aspx)
       Print→ openLedgerMainPrint    (LedgerMainReport_print.aspx)
     ════════════════════════════════════════════════════════════════ */
  const renderSupplierTable = (rows) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className={`${btnCls} sticky top-0`}>
          <tr>
            {["#","Code","Supplier Name","Vendor Type","City","Credit","Debit","Cl. Balance",
              "View","Edit","Print"].map((h) => (
              <th key={h} className={thCls}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className={`divide-y ${isDark ? "divide-gray-700" : "divide-gray-200"}`}>
          {rows.map((r, idx) => (
            <tr key={`sup-${idx}`}
              className={`transition-colors ${idx % 2 === 0
                ? isDark ? "bg-gray-800" : "bg-white"
                : isDark ? "bg-gray-800/60" : "bg-gray-50"}`}
            >
              <td className={`${tdCls} text-center`}>{idx + 1}</td>
              <td className={tdCls}>{toStr(r?.Code || r?.fld_suppliercode)}</td>
              <td className={tdCls}>{toStr(r?.SupplierName || r?.fld_supplierName)}</td>
              <td className={tdCls}>{toStr(r?.VendorType || r?.fld_vendortype)}</td>
              <td className={tdCls}>{toStr(r?.City || r?.fld_city)}</td>
              <td className={`${tdCls} text-right`}>{toStr(r?.Credit)}</td>
              <td className={`${tdCls} text-right`}>{toStr(r?.Debit)}</td>
              <td className={`${tdCls} text-right font-semibold`}>{toStr(r?.Cl_Balance)}</td>

              {/* ✅ View → LedgerInterReport.aspx (gv_supplier_RowCommand "SELECT") */}
              <td className="px-2 py-1 text-center">
                <button
                  onClick={() => openLedgerInterReport(r, "SUPPLIER")}
                  title="Ledger Inter Report"
                  className="p-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <FaEye className="w-3 h-3" />
                </button>
              </td>

              {/* ✅ Edit → Supplier_Mas.aspx (gv_supplier_RowCommand "EDIT") */}
              <td className="px-2 py-1 text-center">
                <button
                  onClick={() => openSupplierMas(r)}
                  title="Edit Supplier Master"
                  className="p-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <FaEdit className="w-3 h-3" />
                </button>
              </td>

              {/* ✅ Print → LedgerMainReport_print.aspx (Imgbtn_printsupplier_Click) */}
              <td className="px-2 py-1 text-center">
                <button
                  onClick={openLedgerMainPrint}
                  title="Print Supplier Report"
                  className={`p-2 rounded-lg ${btnCls}`}
                >
                  <FaPrint className="w-3 h-3" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  /* ════════════════════════════════════════════════════════════════
     DEPARTMENT TABLE  (gv_list)
     Row actions:
       View → openLedgerInterReport  (LedgerInterReport.aspx)
       Sub  → openLedgerSubReport    (LedgerSubReport.aspx)
       Print→ openLedgerPrint        (Ledger_Print.aspx  Type=1/2/3)
     ════════════════════════════════════════════════════════════════ */
  const renderDeptTable = (rows) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className={`${btnCls} sticky top-0`}>
          <tr>
            {["#","Code","Department Name","Total","Branch","View","Sub","Print"].map((h) => (
              <th key={h} className={thCls}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className={`divide-y ${isDark ? "divide-gray-700" : "divide-gray-200"}`}>
          {rows.map((r, idx) => (
            <tr key={`dept-${idx}`}
              className={`transition-colors ${idx % 2 === 0
                ? isDark ? "bg-gray-800" : "bg-white"
                : isDark ? "bg-gray-800/60" : "bg-gray-50"}`}
            >
              <td className={`${tdCls} text-center`}>{idx + 1}</td>
              <td className={tdCls}>{toStr(r?.Code || r?.fld_deptid)}</td>
              <td className={tdCls}>{toStr(r?.DeptName || r?.Branch)}</td>
              <td className={`${tdCls} text-right font-semibold`}>{toStr(r?.Total)}</td>
              <td className={tdCls}>{toStr(r?.Branch)}</td>

              {/* ✅ View → LedgerInterReport.aspx */}
              <td className="px-2 py-1 text-center">
                <button
                  onClick={() => openLedgerInterReport(r, "DEPT")}
                  title="Ledger Inter Report"
                  className="p-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <FaEye className="w-3 h-3" />
                </button>
              </td>

              {/* ✅ Sub → LedgerSubReport.aspx (gv_list_RowCommand) */}
              <td className="px-2 py-1 text-center">
                <button
                  onClick={() => openLedgerSubReport(r, "DEPT")}
                  title="Ledger Sub Report"
                  className="p-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <FaList className="w-3 h-3" />
                </button>
              </td>

              {/* ✅ Print → Ledger_Print.aspx (img_Btn_Dept_Click) */}
              <td className="px-2 py-1 text-center">
                <button
                  onClick={() => openLedgerPrint("DEPT")}
                  title="Print Dept Report"
                  className={`p-2 rounded-lg ${btnCls}`}
                >
                  <FaPrint className="w-3 h-3" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  /* ════════════════════════════════════════════════════════════════
     SPARES RETURN TABLE  (gv_sparesreturn)
     Row actions:
       Sub  → openLedgerSubReport    (LedgerSubReport.aspx)
       Print→ openLedgerPrint        (Ledger_Print.aspx Type=1/2/3/4)
     ════════════════════════════════════════════════════════════════ */
  const renderSparesReturnTable = (rows) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className={`${btnCls} sticky top-0`}>
          <tr>
            {["#","Code","Supplier Name","Total","Branch","Sub","Print"].map((h) => (
              <th key={h} className={thCls}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className={`divide-y ${isDark ? "divide-gray-700" : "divide-gray-200"}`}>
          {rows.map((r, idx) => (
            <tr key={`sr-${idx}`}
              className={`transition-colors ${idx % 2 === 0
                ? isDark ? "bg-gray-800" : "bg-white"
                : isDark ? "bg-gray-800/60" : "bg-gray-50"}`}
            >
              <td className={`${tdCls} text-center`}>{idx + 1}</td>
              <td className={tdCls}>{toStr(r?.Code)}</td>
              <td className={tdCls}>{toStr(r?.SupplierName || r?.Branch)}</td>
              <td className={`${tdCls} text-right font-semibold`}>{toStr(r?.Total)}</td>
              <td className={tdCls}>{toStr(r?.Branch)}</td>

              {/* ✅ Sub → LedgerSubReport.aspx (gv_sparesreturn_RowCommand) */}
              <td className="px-2 py-1 text-center">
                <button
                  onClick={() => openLedgerSubReport(r, "SPARESRETURN")}
                  title="Ledger Sub Report"
                  className="p-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <FaList className="w-3 h-3" />
                </button>
              </td>

              {/* ✅ Print → Ledger_Print.aspx (img_Btn_Sparesreturn_Click) */}
              <td className="px-2 py-1 text-center">
                <button
                  onClick={() => openLedgerPrint("SPARESRETURN")}
                  title="Print Spares Return"
                  className={`p-2 rounded-lg ${btnCls}`}
                >
                  <FaPrint className="w-3 h-3" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  /* ─────────────────────── RENDER ─────────────────────── */
  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-4 lg:p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl lg:text-3xl">LEDGER MAIN REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`p-4 lg:p-6 mt-6 rounded-2xl shadow-xl border
          ${isDark
            ? "bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md border-white/10 text-white"
            : "bg-white text-black border-gray-200"}`}
        >
          {/* ── Header bar ── */}
          <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
            <div className="flex items-center gap-3">
              <h1 className={`text-xl lg:text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Ledger Main Report
              </h1>
              {loading && (
                <span className={`text-xs px-2 py-1 rounded-full animate-pulse
                  ${isDark ? "bg-blue-900 text-blue-300" : "bg-blue-100 text-blue-700"}`}>
                  Loading…
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 text-sm border rounded-lg transition-colors
                  ${isDark ? "border-gray-600 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
              >
                <FaFilter className="w-3 h-3" /> {showFilters ? "Hide Filters" : "Filters"}
              </button>
              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 text-sm border rounded-lg transition-colors
                  ${isDark ? "border-gray-600 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
              >
                <FaArrowLeft className="w-3 h-3" /> Back
              </button>
            </div>
          </div>

          {/* ════════════════════════ FILTER PANEL ════════════════════════ */}
          {showFilters && (
            <div className={`rounded-xl p-4 lg:p-6 mb-6 border
              ${isDark ? "bg-gray-800/60 border-gray-700" : "bg-gray-50 border-gray-200"}`}
            >
              <div className="flex flex-wrap items-center justify-between mb-5 gap-2">
                <h3 className={`text-base font-bold flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}>
                  <FaFilter /> FILTRATION AREA
                </h3>
                <span className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  INFRA SCREEN : B-52
                </span>
              </div>

              {/* Row 1: HO/BO + Mode */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className={`rounded-lg p-4 border ${isDark ? "border-gray-700 bg-gray-900/30" : "border-gray-200 bg-white"}`}>
                  <div className={labelClass}>HO / BO</div>
                  <div className="flex gap-6 mt-2">
                    {["HO", "BO"].map((v) => (
                      <label key={v} className={`flex items-center gap-2 cursor-pointer text-sm
                        ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                        <input type="radio" name="hoBo" value={v}
                          checked={filters.hoBo === v} onChange={handleChange} />
                        {v}
                      </label>
                    ))}
                  </div>
                </div>

                <div className={`rounded-lg p-4 border ${isDark ? "border-gray-700 bg-gray-900/30" : "border-gray-200 bg-white"}`}>
                  <div className={labelClass}>Report Mode</div>
                  <div className="flex gap-6 mt-2">
                    {[["MONTHWISE", "Month Wise"], ["PERIOD", "Period"]].map(([v, l]) => (
                      <label key={v} className={`flex items-center gap-2 cursor-pointer text-sm
                        ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                        <input type="radio" name="periodMode" value={v}
                          checked={filters.periodMode === v} onChange={handleChange} />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Row 2: Date filters */}
              {filters.periodMode === "MONTHWISE" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={labelClass}>Month</label>
                    <select name="month" value={filters.month} onChange={handleChange} className={inputClass}>
                      {months.map((m) => <option key={m.value || "s"} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Year</label>
                    <select name="year" value={filters.year} onChange={handleChange} className={inputClass}>
                      {years.map((y) => <option key={y.value || "s"} value={y.value}>{y.label}</option>)}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={labelClass}>From Date (dd/MM/yyyy)</label>
                    <input type="text" name="fromDate" value={filters.fromDate}
                      onChange={handleChange} className={inputClass} placeholder="dd/MM/yyyy" />
                    {financeYearStart && (
                      <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        Finance Year Start: <b>{financeYearStart}</b>
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>To Date (dd/MM/yyyy)</label>
                    <input type="text" name="toDate" value={filters.toDate}
                      onChange={handleChange} className={inputClass} placeholder="dd/MM/yyyy" />
                  </div>
                </div>
              )}

              {/* Row 3: Main filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {/* Branch */}
                <div>
                  <label className={labelClass}>Branch</label>
                  <select name="branch" value={filters.branch} onChange={handleChange}
                    className={inputClass} disabled={branchType !== "HO"}>
                    {branches.map((b) => <option key={b.value || "s"} value={b.value}>{b.label}</option>)}
                  </select>
                </div>

                {/* Type: Supplier / Equipment */}
                <div>
                  <label className={labelClass}>Type</label>
                  <select name="type" value={filters.type} onChange={handleChange} className={inputClass}>
                    <option value="">Select</option>
                    <option value="1">Supplier</option>
                    <option value="2">Equipment</option>
                  </select>
                </div>

                {/* Supplier / Equipment sub-dropdown */}
                <div>
                  <label className={labelClass}>
                    {filters.type === "1" ? "Supplier" : filters.type === "2" ? "Equipment" : "Supplier / Equipment"}
                    <span className="text-red-400 ml-1">*</span>
                  </label>
                  <select name="rptType" value={filters.rptType} onChange={handleChange}
                    className={inputClass} disabled={!filters.type}>
                    {visibleRptTypeOptions.map((x) => (
                      <option key={x.value || "s"} value={x.value}>{x.label}</option>
                    ))}
                  </select>
                </div>

                {/* Vendor type — only for Supplier */}
                <div className={filters.type !== "1" ? "opacity-40 pointer-events-none" : ""}>
                  <label className={labelClass}>Vendor</label>
                  <select name="vender" value={filters.vender} onChange={handleChange}
                    className={inputClass} disabled={filters.type !== "1"}>
                    <option value="%">All</option>
                    <option value="A">Approved</option>
                    <option value="N">Non Approved</option>
                  </select>
                </div>

                {/* Vehicle Code autocomplete — only for Equipment */}
                <div className={`md:col-span-2 ${filters.type !== "2" ? "opacity-40 pointer-events-none" : ""}`}>
                  <label className={labelClass}>Vehicle Code</label>
                  <input type="text" value={vehicleCodeQuery}
                    onChange={handleVehicleCodeInput}
                    className={inputClass} placeholder="Type to search vehicle code…"
                    list="vcList" disabled={filters.type !== "2"} />
                  <datalist id="vcList">
                    {vehicleCodeOptions.map((o) => (
                      <option key={o.value || o.label} value={o.value}>{o.label}</option>
                    ))}
                  </datalist>
                </div>

                {/* Vehicle Main Type */}
                <div>
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select name="vehicleMainType" value={filters.vehicleMainType}
                    onChange={handleChange} className={inputClass}>
                    {vehicleMainTypes.map((v) => (
                      <option key={v.value || "s"} value={v.value}>{v.label}</option>
                    ))}
                  </select>
                </div>

                {/* Vehicle Type */}
                <div>
                  <label className={labelClass}>Vehicle Type</label>
                  <select name="vehicleType" value={filters.vehicleType}
                    onChange={handleChange} className={inputClass}>
                    {vehicleTypes.map((v) => (
                      <option key={v.value || "s"} value={v.value}>{v.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* ── Action buttons ── */}
              <div className={`flex flex-wrap justify-between items-center gap-3 pt-4 border-t
                ${isDark ? "border-gray-700" : "border-gray-200"}`}>

                <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasAnyResults
                    ? <span>Active: <b className={isDark ? "text-white" : "text-gray-900"}>{activeBlock}</b> — {currentRows.length} row(s)</span>
                    : "Select filters and press Search"}
                </div>

                <div className="flex flex-wrap gap-2 justify-end">
                  {/* Excel */}
                  <button
                    onClick={() => exportToCsv(currentRows, `ledger-${activeBlock.toLowerCase()}.csv`)}
                    disabled={loading || !hasAnyResults}
                    className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40
                      text-white px-4 py-2 rounded-lg text-sm font-semibold"
                  >
                    <FaFileExcel /> Excel
                  </button>

                  {/* ✅ ItemWise → LedgerItemWiseReport.aspx */}
                  {showItemWise && (
                    <button
                      onClick={openItemWisePage}
                      disabled={loading}
                      className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40
                        text-white px-4 py-2 rounded-lg text-sm font-semibold"
                      title="Item Wise Report (LedgerItemWiseReport.aspx)"
                    >
                      <FaList /> Item Wise
                    </button>
                  )}

                  {/* ✅ Supplier Print → LedgerMainReport_print.aspx */}
                  {activeBlock === "SUPPLIER" && supplierRows.length > 0 && (
                    <button
                      onClick={openLedgerMainPrint}
                      disabled={loading}
                      className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-40
                        text-white px-4 py-2 rounded-lg text-sm font-semibold"
                      title="Print Supplier Report (LedgerMainReport_print.aspx)"
                    >
                      <FaPrint /> Supplier Print
                    </button>
                  )}

                  {/* ✅ Block Print → Ledger_Print.aspx */}
                  {hasAnyResults && (
                    <button
                      onClick={() => openLedgerPrint(activeBlock)}
                      disabled={loading}
                      className={`flex items-center gap-2 ${btnCls} disabled:opacity-40
                        px-4 py-2 rounded-lg text-sm font-semibold`}
                      title="Print (Ledger_Print.aspx)"
                    >
                      <FaPrint /> Print
                    </button>
                  )}

                  {/* Search */}
                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className={`flex items-center gap-2 ${btnCls} disabled:opacity-40
                      px-5 py-2 rounded-lg text-sm font-semibold shadow-lg`}
                  >
                    <FaSearch /> {loading ? "Searching…" : "Search"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ════════════════════════ RESULTS ════════════════════════ */}
          {!hasAnyResults ? (
            <div className="text-center py-20">
              <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6
                ${isDark ? "bg-gray-700" : "bg-gray-100"}`}>
                <FaInfoCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                No Records Found
              </h3>
              <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Select filters and click Search to load results.
              </p>
            </div>
          ) : (
            <div className="space-y-6">

              {/* Block tabs */}
              <div className="flex flex-wrap gap-2">
                {blocks.map(({ key, label, rows }) => (
                  <button key={key} onClick={() => setActiveBlock(key)}
                    className={`px-4 py-2 rounded-lg border text-sm font-semibold transition-colors
                      ${activeBlock === key
                        ? `${btnCls} border-transparent`
                        : isDark
                          ? "border-gray-600 text-gray-200 hover:bg-gray-800"
                          : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
                  >
                    {label} ({rows.length})
                  </button>
                ))}
              </div>

              {/* Block content */}
              <div className={`rounded-xl border overflow-hidden shadow-sm
                ${isDark ? "border-gray-700" : "border-gray-200"}`}>

                {/* Block header with print button */}
                <div className={`px-4 py-3 flex justify-between items-center
                  ${isDark ? "bg-gray-800 text-white" : "bg-gray-50 text-gray-900"}`}>
                  <span className="font-bold text-sm">
                    {blocks.find((b) => b.key === activeBlock)?.label || activeBlock}
                  </span>
                  <div className="flex gap-2">
                    {/* ✅ Excel export */}
                    <button
                      onClick={() => exportToCsv(currentRows, `${activeBlock.toLowerCase()}-ledger.csv`)}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700
                        text-white rounded-lg font-semibold"
                    >
                      <FaFileExcel /> CSV
                    </button>

                    {/* ✅ Print (supplier uses LedgerMainReport_Print, others use LedgerPrint) */}
                    {activeBlock === "SUPPLIER" ? (
                      <button
                        onClick={openLedgerMainPrint}
                        className={`flex items-center gap-1 px-3 py-1.5 text-xs ${btnCls} rounded-lg font-semibold`}
                        title="LedgerMainReport_print.aspx"
                      >
                        <FaPrint /> Print
                      </button>
                    ) : (
                      <button
                        onClick={() => openLedgerPrint(activeBlock)}
                        className={`flex items-center gap-1 px-3 py-1.5 text-xs ${btnCls} rounded-lg font-semibold`}
                        title="Ledger_Print.aspx"
                      >
                        <FaPrint /> Print
                      </button>
                    )}
                  </div>
                </div>

                {/* Table */}
                {activeBlock === "OWN"          && renderVehicleTable(ownRows, "OWN")}
                {activeBlock === "HIRE"         && renderVehicleTable(hireRows, "HIRE")}
                {activeBlock === "CP"           && renderVehicleTable(cpRows, "CP")}
                {activeBlock === "SUPPLIER"     && renderSupplierTable(supplierRows)}
                {activeBlock === "DEPT"         && renderDeptTable(deptRows)}
                {activeBlock === "SPARESRETURN" && renderSparesReturnTable(sparesReturnRows)}
              </div>

              {/* ── Legend: linked page guide ── */}
              <div className={`rounded-xl p-4 border text-xs
                ${isDark ? "bg-gray-800/40 border-gray-700 text-gray-400" : "bg-blue-50 border-blue-100 text-gray-600"}`}>
                <p className="font-bold mb-2 text-blue-500">Linked Pages (Row Actions):</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <span><span className="inline-block w-3 h-3 rounded-full bg-indigo-600 mr-1" />
                    <b>View</b> → LedgerInterReport</span>
                  <span><span className="inline-block w-3 h-3 rounded-full bg-purple-600 mr-1" />
                    <b>Sub</b> → LedgerSubReport</span>
                  <span><span className="inline-block w-3 h-3 rounded-full bg-amber-500 mr-1" />
                    <b>Edit</b> → SupplierMas (Supplier only)</span>
                  <span><span className="inline-block w-3 h-3 rounded-full bg-orange-600 mr-1" />
                    <b>Supplier Print</b> → LedgerMainReport_Print</span>
                  <span><b>Print</b> → Ledger_Print (Type 1/2/3/4)</span>
                  <span><b>Item Wise</b> → LedgerItemWiseReport</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <ToastContainer
          position="top-right"
          autoClose={2500}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
}